// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FaUser, FaSignInAlt, FaUserPlus, FaHome, FaSignOutAlt, FaBars, FaTimes } from "react-icons/fa";
import UserMenu from "./auth/UserMenu";
import "./Navbar.css";

const Navbar = () => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isMobileView, setMobileView] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setMobileView(mobile);
      if (!mobile) {
        setMenuOpen(false); // Ferme le menu mobile quand on passe en vue desktop
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    handleResize();
    handleScroll();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const menuItems = [
    { label: "Accueil", path: "/" },
    { label: "À Propos", path: "/a-propos" },
    { label: "Auberge", path: "/auberge" },
    { label: "Gîtes", path: "/gites" },
    { label: "La Ferme", path: "/la-ferme" },
    { label: "Tarifs", path: "/tarifs" },
    { label: "Notre Région", path: "/notre-region" },
    { label: "Contact", path: "/contact" },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      setMenuOpen(false);
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
    }
  };

  // Rendu des boutons d'authentification pour la vue mobile
  const renderMobileAuthButtons = () => (
    <div className="mobile-auth-buttons">
      <button 
        className="auth-button login"
        onClick={() => {
          navigate('/connexion');
          setMenuOpen(false);
        }}
      >
        <FaSignInAlt className="auth-icon" />
        <span>Connexion</span>
      </button>
      <button 
        className="auth-button signup"
        onClick={() => {
          navigate('/inscription');
          setMenuOpen(false);
        }}
      >
        <FaUserPlus className="auth-icon" />
        <span>Inscription</span>
      </button>
    </div>
  );

  // Rendu du menu utilisateur pour la vue mobile
  const renderMobileUserMenu = () => (
    <div className="mobile-user-menu">
      <div className="user-info">
        <p className="user-name">{currentUser?.name || 'Utilisateur'}</p>
        <p className="user-email">{currentUser?.email || ''}</p>
      </div>
      <button 
        className="dropdown-item"
        onClick={() => {
          navigate('/mon-compte');
          setMenuOpen(false);
        }}
      >
        <FaUser className="dropdown-icon" />
        <span>Mon compte</span>
      </button>
      {currentUser?.role === 'admin' && (
        <button 
          className="dropdown-item"
          onClick={() => {
            navigate('/admin');
            setMenuOpen(false);
          }}
        >
          <FaHome className="dropdown-icon" />
          <span>Tableau de bord</span>
        </button>
      )}
      <button 
        className="dropdown-item logout"
        onClick={handleLogout}
      >
        <FaSignOutAlt className="dropdown-icon" />
        <span>Déconnexion</span>
      </button>
    </div>
  );

  return (
    <nav className={`navbar ${isScrolled ? "scrolled" : "initial-transparent"}`}>
      <div className="navbar-container">
        <Link to="/" className="logo-link" onClick={() => setMenuOpen(false)}>
          <img 
            src="/logo.png" 
            alt="Auberge de Peguilhan" 
            className="navbar-logo" 
          />
        </Link>

        <div className="nav-links">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? "active" : ""}`}
              onClick={() => isMobileView && setMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="auth-buttons">
          {currentUser ? (
            <UserMenu />
          ) : (
            <>
              <button 
                className="auth-button login"
                onClick={() => navigate('/connexion')}
              >
                <FaSignInAlt className="auth-icon" />
                <span>Connexion</span>
              </button>
              <button 
                className="auth-button signup"
                onClick={() => navigate('/inscription')}
              >
                <FaUserPlus className="auth-icon" />
                <span>Inscription</span>
              </button>
            </>
          )}
        </div>

        <div 
          className="menu-toggle" 
          onClick={() => setMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="menu-text">Menu</span>
          <div className="menu-icon">
            {isMenuOpen ? <FaTimes /> : <FaBars />}
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`menu-overlay ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <div 
          className="close-icon" 
          onClick={() => setMenuOpen(false)}
        >
          <FaTimes />
        </div>
        
        <ul className="mobile-menu-links">
          {menuItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={location.pathname === item.path ? 'active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                {item.label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Auth section for mobile */}
        {currentUser ? renderMobileUserMenu() : renderMobileAuthButtons()}
      </div>
    </nav>
  );
};

export default Navbar;